export const SALES_ESTIMATE_SECTION_KEYS = [
    'discovery_design',
    'record_to_report',
    'order_to_cash',
    'revenue_management',
    'advanced_projects',
    'procure_to_pay',
    'inventory_management',
    'third_party_system_support',
    'integrations',
    'data_migration',
    'optional_modules',
    'project_management',
    'testing',
    'go_live',
    'post_go_live',
];
/** Short human label for an {@link EstimationMethod} (badge / chat). */
export function estimationMethodLabel(method) {
    switch (method) {
        case 'reference_corpus':
            return 'Reference corpus';
        case 'deal_snapshot':
            return 'Similar past deals';
        case 'ai':
            return 'AI inference';
    }
}
/** Map a grounding source key onto the user-facing method ('model' is pure AI inference). */
export function methodFromSourceKey(sourceKey) {
    return sourceKey === 'reference_corpus' || sourceKey === 'deal_snapshot' ? sourceKey : 'ai';
}
/** The detail suffix only - e.g. `5 past items, likely 12-20h` (empty when nothing to add). */
export function provenanceDetail(input) {
    const detail = [];
    if (input.method !== 'ai' && input.pastItemCount && input.pastItemCount > 0) {
        detail.push(`${input.pastItemCount} past item${input.pastItemCount === 1 ? '' : 's'}`);
    }
    if (typeof input.rangeLow === 'number' && typeof input.rangeHigh === 'number') {
        detail.push(`likely ${input.rangeLow}-${input.rangeHigh}h`);
    }
    return detail.join(', ');
}
export function formatProvenanceLine(input) {
    const refs = input.refIndexes && input.refIndexes.length > 0 ? ` [${input.refIndexes.join(', ')}]` : '';
    const detail = provenanceDetail(input);
    return `${estimationMethodLabel(input.method)}${refs}${detail ? ` - ${detail}` : ''}`;
}
/**
 * Offshore fraction (0–1) for a role, given its basis and optional stored
 * offshore percentage. `onshore` ⇒ 0, `offshore` ⇒ 1, `hybrid` ⇒ the clamped
 * `offshorePercent / 100`. The configurable default is applied where a hybrid
 * role is seeded (admin config), so a missing value here is a degenerate case
 * and resolves to 0% offshore rather than a hard-coded default.
 */
function offshoreFraction(basis, offshorePercent) {
    if (basis === 'onshore')
        return 0;
    if (basis === 'offshore')
        return 1;
    const pct = offshorePercent ?? 0;
    return Math.min(100, Math.max(0, pct)) / 100;
}
/**
 * Canonical cost for a role under the given pricing basis. The single source of
 * truth shared by the editable table, the view-only pane, the resourcing
 * summary, the markdown/Word exports and the spreadsheet preview.
 *
 * Hybrid is the exact linear interpolation `onshore*(1-p) + offshore*p`, which
 * is correct because `cost.onshore`/`cost.offshore` are each `hours × rate`.
 */
export function resolveRoleCost(entry, basis, offshorePercent) {
    const p = offshoreFraction(basis, offshorePercent);
    return entry.cost.onshore * (1 - p) + entry.cost.offshore * p;
}
/**
 * Per-hour rate for a role at the given basis, from the entry's carried rates.
 * Used to cost a role whose engine hours are 0 (a manually-added role), where the
 * usual "scale the engine cost" path divides by zero. Returns 0 when rates absent.
 * Shared by the editable table (apps/web) and the resource-list service (apps/api)
 * so an overridden/manual role costs identically everywhere.
 */
export function roleUnitRate(entry, basis, offshorePercent) {
    const onshore = entry.onshore_rate ?? 0;
    const offshore = entry.offshore_rate ?? 0;
    if (basis === 'onshore')
        return onshore;
    if (basis === 'offshore')
        return offshore;
    const p = (offshorePercent ?? 0) / 100;
    return onshore * (1 - p) + offshore * p;
}
/**
 * Splits a role's hours into onshore- and offshore-delivered buckets for the
 * muted breakdown lines and the Resourcing Model summary cards. Hybrid roles
 * contribute fractionally to both buckets.
 */
export function resolveRoleHoursSplit(entry, basis, offshorePercent) {
    const p = offshoreFraction(basis, offshorePercent);
    return { onshoreHours: entry.hours * (1 - p), offshoreHours: entry.hours * p };
}
/**
 * Splits a role's cost into onshore- and offshore-delivered buckets, mirroring
 * {@link resolveRoleHoursSplit}. The two buckets always sum to
 * {@link resolveRoleCost}.
 */
export function resolveRoleCostSplit(entry, basis, offshorePercent) {
    const p = offshoreFraction(basis, offshorePercent);
    return { onshoreCost: entry.cost.onshore * (1 - p), offshoreCost: entry.cost.offshore * p };
}
/**
 * Normalize a stored pricing-selection value into a {@link RolePricingSelection}.
 * Tolerates the legacy flat-string shape (`"onshore"`) so a not-yet-reset row or
 * an in-flight snapshot can't crash readers; unknown values default to onshore.
 */
export function coerceRolePricing(value) {
    if (typeof value === 'string') {
        return { basis: value };
    }
    if (value && typeof value === 'object' && 'basis' in value) {
        const sel = value;
        return sel.basis === 'hybrid'
            ? { basis: 'hybrid', offshorePercent: sel.offshorePercent }
            : { basis: sel.basis };
    }
    return { basis: 'onshore' };
}
/**
 * Resolve the effective basis + offshore percentage for a role from the stored
 * selection map: missing roles read as `onshore`. A hybrid role's offshore % is
 * seeded from admin config at selection time, so a missing value here is a
 * degenerate case and resolves to 0 rather than a hard-coded default. The single
 * accessor used by the table, view-only pane, summary, exports and the service.
 */
export function readRolePricing(map, roleId) {
    const sel = coerceRolePricing(map?.[roleId]);
    return { basis: sel.basis, offshorePercent: sel.offshorePercent ?? 0 };
}
/**
 * Resolve a role's effective allocation (% of the grand total + hours) from the
 * stored selection map. When the role carries an `allocationOverride` the stored
 * percent/hours win; otherwise the engine-computed hours are used and the percent
 * is derived as `hours / base` (`base` = grand total, typically
 * `resourcingSnapshot.totals.total_hours`). `base = 0` yields `0%`. Reads the raw
 * map value (not via `coerceRolePricing`, which drops the override). Used by the
 * table to render the muted %, the overridden state, and the over-allocation sum.
 */
export function readRoleAllocation(map, roleId, entry, base) {
    const value = map?.[roleId];
    const override = value && typeof value === 'object'
        ? value.allocationOverride
        : undefined;
    if (override) {
        return {
            percent: override.percent,
            hours: override.hours,
            isOverridden: true,
            originalHours: override.originalHours,
        };
    }
    return {
        // Prefer the persisted planned fraction (the `ai` model pins a fixed share
        // like PM's pm_percent, which must read exactly) over realized hours/total,
        // which floors off. Absent (static models / old snapshots) ⇒ hours/total.
        percent: typeof entry.allocation_fraction === 'number'
            ? entry.allocation_fraction
            : base > 0
                ? entry.hours / base
                : 0,
        hours: entry.hours,
        isOverridden: false,
        originalHours: entry.hours,
    };
}
//# sourceMappingURL=sales-estimate.js.map